import { useEffect, useMemo, useState } from 'react';
import programHero1 from './assets/news/news1.jpg';
import programHero2 from './assets/news/news4.jpg';
import programHero3 from './assets/news/news7.jpg';

// Icon Components
const AssessmentIcons = {
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="M17 5H9.5a1.5 1.5 0 0 0-1.5 1.5v12a1.5 1.5 0 0 0 1.5 1.5H17" />
      <path d="M6 12h3v8H6z" />
      <path d="M9 8h3v12H9z" />
      <path d="M12 5h3v15h-3z" />
      <path d="M15 9h3v11h-3z" />
    </svg>
  ),
  clipboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <path d="M9 14h6" />
      <path d="M9 10h6" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  trending: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  lightbulb: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
      <polygon points="12 2 15.09 10.26 24 10.26 17.55 16.52 19.64 24.78 12 18.52 4.36 24.78 6.45 16.52 0 10.26 8.91 10.26" />
    </svg>
  ),
  award: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  ),
};

const getAssessmentIcon = (iconType) => {
  return AssessmentIcons[iconType] || AssessmentIcons.chart;
};

const DEFAULT_SUBJECTS = [
  {
    name: 'English',
    description:
      'Strengthen speaking, reading, and writing through guided literacy, structured discussions, and steady vocabulary growth.',
    image: programHero1,
  },
  {
    name: 'Mathematics',
    description:
      'Build number sense, problem solving, and logical thinking with step-by-step practice and applied projects.',
    image: programHero2,
  },
  {
    name: 'Science',
    description:
      'Hands-on experiments and inquiry that nurture curiosity, observation, and scientific reasoning.',
    image: programHero3,
  },
  {
    name: 'ICT',
    description:
      'Digital literacy, creative technology, and foundational computing skills that support modern learning.',
    image: programHero1,
  },
  {
    name: 'Humanities',
    description:
      'Social and cultural understanding that develops empathy, citizenship, and global awareness.',
    image: programHero2,
  },
  {
    name: 'Arts',
    description:
      'Creative expression through music, visual arts, and performance to build confidence and imagination.',
    image: programHero3,
  },
  {
    name: 'Physical Education',
    description:
      'Fitness, coordination, and sportsmanship through safe and structured physical activities.',
    image: programHero1,
  },
  {
    name: 'Character Building',
    description:
      'Faith, integrity, discipline, and care for others developed through reflection and daily practice.',
    image: programHero2,
  },
];

const SUBJECT_FOCUS = {
  English: ['Speaking and presentation', 'Reading comprehension', 'Academic writing'],
  Mathematics: ['Number sense and algebra', 'Problem solving', 'Data and reasoning'],
  Science: ['Inquiry and lab skills', 'Scientific literacy', 'Real-world investigations'],
  ICT: ['Digital citizenship', 'Creative technology', 'Foundational coding'],
  Humanities: ['Civic awareness', 'Cultural understanding', 'Critical discussion'],
  Arts: ['Creative expression', 'Performance confidence', 'Design thinking'],
  'Physical Education': ['Fitness and coordination', 'Teamwork and sportsmanship', 'Healthy habits'],
  'Character Building': ['Integrity and empathy', 'Leadership habits', 'Service mindset'],
};


function EducationProgramPage({ page, onNavigate }) {
  const heroSlides = page?.hero_slides || [programHero1, programHero2, programHero3];
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.edu-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const heroTitle = page?.title || 'Education Program';
  const heroSummary =
    page?.summary ||
    'A learning pathway from early years to secondary that builds character, confidence, and academic strength.';

  const programLevels = page?.program_levels || [
    {
      anchor: 'preschool',
      title: 'Preschool',
      subtitle: 'Ages 3-4',
      description:
        'Play-based discovery, social-emotional growth, and foundational routines that nurture curiosity and confidence.',
      image: programHero1,
      href: '#preschool',
      slug: 'preschool',
    },
    {
      anchor: 'kindergarten',
      title: 'Kindergarten',
      subtitle: 'Ages 5-6',
      description:
        'Literacy, numeracy, and character formation supported by joyful learning and structured exploration.',
      image: programHero2,
      href: '#kindergarten',
      slug: 'kindergarten',
    },
    {
      anchor: 'primary',
      title: 'Primary Program',
      subtitle: 'Grades 1-6',
      description:
        'Core academics, bilingual support, and project-based learning that build critical thinking and resilience.',
      image: programHero3,
      href: '#primary',
      slug: 'primary',
    },
    {
      anchor: 'secondary',
      title: 'Secondary Program',
      subtitle: 'Grades 7-12',
      description:
        'Leadership, academic depth, and university readiness for a confident next step.',
      image: programHero1,
      href: '#secondary',
      slug: 'secondary',
    },
  ];

  const curriculumOverview = page?.curriculum_overview || {
    title: 'National & International Curriculum Pathway',
    summary:
      'We blend the national curriculum with Cambridge perspectives so every student grows in faith, character, and academic readiness from Preschool to Secondary.',
    pillars: [
      {
        title: 'National Foundations',
        description: 'Strong alignment with local standards, values education, and Bahasa Indonesia literacy.',
      },
      {
        title: 'Cambridge Perspective',
        description: 'Inquiry, language mastery, and global awareness woven across core subjects.',
      },
      {
        title: 'Holistic Growth (SDP)',
        description: 'Co- and extra-curricular programs nurture leadership, service, and life skills.',
      },
    ],
    tracks: [
      {
        title: 'Preschool',
        subtitle: 'Ages 3-4',
        description:
          'Play-based learning that builds curiosity, social confidence, and early literacy through guided exploration and storytelling.',
        highlights: [
          'National values and early Bahasa Indonesia exposure',
          'Cambridge-inspired inquiry for language and numeracy readiness',
          'Daily routines that shape independence and learning habits',
        ],
        image: programHero2,
      },
      {
        title: 'Kindergarten',
        subtitle: 'Ages 5-6',
        description:
          'Structured exploration that strengthens reading readiness, number sense, and character formation.',
        highlights: [
          'Balanced local curriculum and Cambridge-inspired foundations',
          'Early bilingual development through speaking and listening',
          'Hands-on discovery that builds curiosity and confidence',
        ],
        image: programHero3,
      },
      {
        title: 'Primary ',
        subtitle: 'Grades 1-6 ',
        description:
          'National standards with Cambridge enrichment to build bilingual confidence, critical thinking, and project-based mastery.',
        highlights: [
          'Core competencies and character education aligned with national standards',
          'Cambridge-aligned depth for English, Math, and Science',
          'Projects that apply skills to real-world contexts',
        ],
        image: programHero1,
      },
      {
        title: 'Secondary ',
        subtitle: 'Grades 7-9 ',
        description:
          "Permata Harapan Secondary's curriculum consists of three interconnected components — the Intra Curriculum (Kurikulum Inti), the Extra Curriculum (ECA), and the Co-Curriculum (CCA). Together, Co-Curriculum and Extra Curriculum support the Students Development Program (SDP), nurturing well-rounded students academically, personally, and socially.",
        highlights: [
          'Intra Curriculum as the academic core and formal learning foundation',
          'Co-Curriculum (CCA) builds skills, values, and competencies aligned to learning goals',
          'Extra Curriculum (ECA) expands interests, talents, and enrichment pathways',
        ],
        components: [
          'Intra Curriculum (Kurikulum Inti)',
          'Co-Curriculum (CCA)',
          'Extra Curriculum (ECA)',
          'Students Development Program (SDP)',
        ],
        image: programHero2,
      },
    ],
    note:
      'The integration of Cambridge frameworks with the national curriculum builds academic strength, character, and global readiness across all levels.',
  };

  const rawSubjects = page?.subjects_offered || DEFAULT_SUBJECTS;
  const normalizedSubjects = useMemo(
    () =>
      rawSubjects.map((subject, index) => {
        if (typeof subject === 'string') {
          return {
            name: subject,
            description: 'A focused subject pathway that builds understanding, skills, and curiosity.',
            image: DEFAULT_SUBJECTS[index % DEFAULT_SUBJECTS.length].image,
          };
        }

        return {
          name: subject.name || `Subject ${index + 1}`,
          description:
            subject.description ||
            'A focused subject pathway that builds understanding, skills, and curiosity.',
          image: subject.image || DEFAULT_SUBJECTS[index % DEFAULT_SUBJECTS.length].image,
        };
      }),
    [rawSubjects]
  );

  const [activeSubject, setActiveSubject] = useState(normalizedSubjects[0]);


  const learningApproach = page?.learning_approach || {
    pedagogicalModel: {
      title: '5E Instructional Model',
      subtitle: 'School-wide approach to learning',
      description: 'Permata Harapan Secondary adopts the 5E Instructional Model as a comprehensive framework for teaching and learning across all subjects.',
      phases: [
        {
          phase: 'Engage',
          description: 'Activate curiosity and prior knowledge',
          details: 'Begin lessons by connecting to students\' interests and existing understanding, creating excitement for learning.',
          icon: 'lightbulb',
        },
        {
          phase: 'Explore',
          description: 'Provide hands-on inquiry-based activities',
          details: 'Students engage in direct investigation and discovery, building foundational understanding through experience.',
          icon: 'target',
        },
        {
          phase: 'Explain',
          description: 'Facilitate deep conceptual understanding',
          details: 'Teachers guide students to articulate and refine their understanding through discussion and reflection.',
          icon: 'clipboard',
        },
        {
          phase: 'Elaborate',
          description: 'Extend learning through real-world application',
          details: 'Students apply concepts to new contexts and solve authentic problems, deepening mastery.',
          icon: 'zap',
        },
        {
          phase: 'Evaluate',
          description: 'Reflect, assess, and provide feedback',
          details: 'Continuous assessment and reflection ensure students understand progress and next steps.',
          icon: 'check',
        },
      ],
    },
    signatureStrategies: [
      {
        title: 'Inquiry-Based Learning (IBL)',
        description:
          'Students explore big questions through investigation, discussion, and reflection to build curiosity and reasoning.',
        outcomes: ['Questioning and analysis', 'Evidence-backed conclusions', 'Reflective thinking habits'],
        image: programHero1,
        icon: 'target',
      },
      {
        title: 'Project-Based Learning (PBL)',
        description:
          'Integrated projects connect multiple subjects and turn learning into meaningful real-world outcomes.',
        outcomes: ['Research and presentation', 'Collaboration and leadership', 'Creative problem solving'],
        image: programHero2,
        icon: 'zap',
      },
      {
        title: 'Problem-Based Learning (PrBL)',
        description:
          'Students tackle authentic challenges that require critical thinking, collaboration, and innovative solutions.',
        outcomes: ['Complex problem solving', 'Systems thinking', 'Adaptive learning strategies'],
        image: programHero3,
        icon: 'chart',
      },
    ],
    integration: 'These strategies are integrated across all subjects, especially in Humanities, Science, and Global Perspectives.',
  };

  const assessmentSystem = page?.assessment_system || {
    title: "How students' progress is evaluated",
    summary:
      'We use a balanced assessment system that values daily growth, authentic performance, and mastery of standards.',
    points: [
      {
        title: 'Diagnostic Baselines',
        description:
          'Early assessments identify strengths and learning needs so teaching targets the right starting point.',
        iconType: 'chart',
        details: [
          'Initial skill mapping and learning profile',
          'Personalized learning pathway design',
          'Baseline data for progress tracking',
        ],
      },
      {
        title: 'Formative Checks',
        description:
          'Observations, exit tickets, and short quizzes guide weekly instruction and support individual progress.',
        iconType: 'clipboard',
        details: [
          'Daily classroom observations and notes',
          'Weekly exit tickets and quick checks',
          'Real-time feedback for adjustment',
        ],
      },
      {
        title: 'Performance Tasks',
        description:
          'Projects, presentations, and practical tasks show how students apply knowledge and skills in context.',
        iconType: 'target',
        details: [
          'Project-based assessments and portfolios',
          'Presentations and demonstrations',
          'Real-world application evidence',
        ],
      },
      {
        title: 'Summative Assessments',
        description:
          'Mid-term and end-term assessments measure mastery of key standards and learning objectives.',
        iconType: 'check',
        details: [
          'Mid-term and end-term exams',
          'Standardized benchmark assessments',
          'Mastery verification and certification',
        ],
      },
      {
        title: 'Reporting & Goal Setting',
        description:
          'Parents receive clear progress reports, feedback, and next-step goals for continued growth.',
        iconType: 'trending',
        details: [
          'Detailed progress reports and analytics',
          'Parent-teacher conferences quarterly',
          'Collaborative goal-setting sessions',
        ],
      },
    ],
    methodology: [
      {
        phase: 'Continuous Monitoring',
        description: 'Daily observations and informal checks keep us informed of each student\'s progress.',
      },
      {
        phase: 'Data-Driven Decisions',
        description: 'Assessment data guides instructional adjustments and targeted interventions.',
      },
      {
        phase: 'Transparent Communication',
        description: 'Regular updates ensure parents and students understand progress and next steps.',
      },
      {
        phase: 'Growth Mindset Focus',
        description: 'Feedback emphasizes effort, improvement, and learning from mistakes.',
      },
    ],
  };

  const specialPrograms = page?.special_programs || [
    {
      title: 'Bridging Program',
      description:
        'Targeted support to help new students transition smoothly with language, routines, and learning expectations.',
      image: programHero1,
    },
    {
      title: 'Clubs & Enrichment',
      description:
        'Student clubs in arts, STEM, sports, and creativity that nurture interests beyond the classroom.',
      image: programHero2,
    },
    {
      title: 'Student Leadership',
      description:
        'Leadership roles, mentoring, and service projects that build confidence, responsibility, and teamwork.',
      image: programHero3,
    },
    {
      title: 'Community Service',
      description:
        'Outreach activities that foster empathy, gratitude, and social responsibility in real-world settings.',
      image: programHero1,
    },
    {
      title: 'ECA (Extra-Curricular Activities)',
      description:
        'Weekly activities in sports, arts, STEM, and leadership that help students explore interests and build confidence.',
      image: programHero2,
    },
    {
      title: 'Talent Programs',
      description:
        'Coaching and showcase opportunities for students with strengths in music, athletics, performance, and academics.',
      image: programHero3,
    },
  ];

  useEffect(() => {
    if (!normalizedSubjects.length) {
      return;
    }

    if (!activeSubject) {
      setActiveSubject(normalizedSubjects[0]);
      return;
    }

    const stillExists = normalizedSubjects.some(
      (subject) => subject.name === activeSubject.name
    );

    if (!stillExists) {
      setActiveSubject(normalizedSubjects[0]);
    }
  }, [normalizedSubjects, activeSubject]);

  return (
    <section className="education-program-page">
      <section className="education-hero" id="education-overview">
        <div className="education-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`education-hero-slide ${index === activeSlide ? 'active' : ''}`}
            >
              <img src={slide} alt="Education program campus" />
            </div>
          ))}
        </div>
        <div className="education-hero-overlay" aria-hidden="true" />
        <div className="education-hero-content">
          <p className="education-hero-kicker">Education Program</p>
          <h2>{heroTitle}</h2>
          <p>{heroSummary}</p>
          <div className="education-hero-dots" role="tablist" aria-label="Education program hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`education-hero-dot ${index === activeSlide ? 'active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="education-levels" id="program-levels">
        <div className="education-levels-header">
          <p className="education-levels-kicker">Programs</p>
          <h3>Preschool, Kindergarten, Primary, and Secondary</h3>
          <p>
            Each stage is designed to meet students at their level while growing faith, skills, and confidence.
          </p>
        </div>

        <div className="education-levels-grid">
          {programLevels.map((level) => (
            <button
              className="education-level-card edu-animate"
              key={level.title}
              id={level.anchor}
              onClick={() => onNavigate && onNavigate(level.slug, level.anchor)}
              type="button"
              aria-label={`Open ${level.title} details`}
            >
              <div className="education-level-media">
                <img src={level.image} alt={level.title} />
              </div>
              <div className="education-level-body">
                <div className="education-level-top">
                  <p className="education-level-title">{level.title}</p>
                  <span className="education-level-subtitle">{level.subtitle}</span>
                </div>
                <p className="education-level-description">{level.description}</p>
                <span className="education-level-link">
                  View details
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="curriculum-overview" id="curriculum-overview">
        <div className="curriculum-overview-header">
          <p className="curriculum-overview-kicker">Curriculum Overview</p>
          <h3>{curriculumOverview.title}</h3>
          <p>{curriculumOverview.summary}</p>
          <div className="curriculum-overview-pillars">
            {curriculumOverview.pillars.map((pillar) => (
              <div className="curriculum-pillar edu-animate" key={pillar.title}>
                <p className="curriculum-pillar-title">{pillar.title}</p>
                <p>{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="curriculum-overview-grid">
          {curriculumOverview.tracks.map((item, index) => (
            <article
              className="curriculum-card edu-animate"
              key={item.title}
              style={{ animationDelay: `${index * 120}ms` }}
            >
              <div className="curriculum-card-body">
                <div className="curriculum-card-top">
                  <p className="curriculum-card-kicker">{item.subtitle}</p>
                </div>
                <h4>{item.title}</h4>
                <p className="curriculum-card-description">{item.description}</p>
                <ul className="curriculum-card-list">
                  {item.highlights.map((highlight) => (
                    <li key={highlight}>
                      <span aria-hidden="true">•</span>
                      {highlight}
                    </li>
                  ))}
                </ul>
                {item.components && (
                  <div className="curriculum-components">
                    <p className="curriculum-components-title">Curriculum Structure</p>
                    <div className="curriculum-components-grid">
                      {item.components.map((component) => (
                        <span className="curriculum-component" key={component}>
                          {component}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="curriculum-card-media">
                <img src={item.image} alt={item.title} />
              </div>
            </article>
          ))}
        </div>

        <div className="curriculum-overview-note edu-animate">{curriculumOverview.note}</div>
      </section>

      <section className="subjects-offered" id="subjects-offered">
        <div className="subjects-offered-header">
          <p className="subjects-offered-kicker">Subjects Offered</p>
          <h3>Balanced Academics, Creativity, and Character Growth</h3>
          <p>
            A well-rounded subject mix that strengthens mastery, nurtures curiosity, and shapes students into confident learners.
          </p>
        </div>
        <div className="subjects-offered-stats edu-animate">
          <div className="subjects-stat">
            <span className="subjects-stat-value">{normalizedSubjects.length}</span>
            <span className="subjects-stat-label">Core Subjects</span>
          </div>
          <div className="subjects-stat">
            <span className="subjects-stat-value">3</span>
            <span className="subjects-stat-label">Learning Domains</span>
          </div>
          <div className="subjects-stat">
            <span className="subjects-stat-value">100%</span>
            <span className="subjects-stat-label">Cambridge + Local Alignment</span>
          </div>
        </div>

        <div className="subjects-offered-layout">
          <div className="subjects-offered-list edu-animate">
            {normalizedSubjects.map((subject) => (
              <button
                key={subject.name}
                type="button"
                className={`subject-card ${activeSubject?.name === subject.name ? 'active' : ''}`}
                onClick={() => setActiveSubject(subject)}
              >
                <div>
                  <h4>{subject.name}</h4>
                  <p>{subject.description}</p>
                </div>
                <span className="subject-card-arrow" aria-hidden="true">→</span>
              </button>
            ))}
          </div>

          {activeSubject && (
            <div className="subject-feature edu-animate">
              <div className="subject-feature-header">
                <div>
                  <p className="subject-feature-kicker">Featured Focus</p>
                  <h4>{activeSubject.name}</h4>
                </div>
                <span className="subject-feature-tag">Balanced pathway</span>
              </div>
              <div className="subject-feature-media">
                <img src={activeSubject.image} alt={activeSubject.name} />
              </div>
              <p className="subject-feature-description">{activeSubject.description}</p>
              <ul className="subject-feature-list">
                {(SUBJECT_FOCUS[activeSubject.name] || [
                  'Concept mastery with clear learning targets',
                  'Practice, feedback, and reflection cycles',
                  'Projects that connect to real contexts',
                ]).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <section className="learning-approach" id="learning-approach">
        <div className="learning-approach-header">
          <p className="learning-approach-kicker">Learning Approach</p>
          <h3>5E Model, Inquiry, Project & Problem-Based Learning</h3>
          <p>
            A comprehensive framework that develops curiosity, collaboration, and mastery across every level.
          </p>
        </div>

        {/* 5E Instructional Model */}
        <div className="five-e-model edu-animate">
          <div className="five-e-header">
            <h4>{learningApproach.pedagogicalModel.title}</h4>
            <p className="five-e-subtitle">{learningApproach.pedagogicalModel.subtitle}</p>
            <p className="five-e-description">{learningApproach.pedagogicalModel.description}</p>
          </div>
          <div className="five-e-phases">
            {learningApproach.pedagogicalModel.phases.map((phase, index) => (
              <div className="five-e-phase" key={phase.phase} style={{ animationDelay: `${index * 100}ms` }}>
                <div className="phase-number">{index + 1}</div>
                <div className="phase-icon">
                  {getAssessmentIcon(phase.icon)}
                </div>
                <h5>{phase.phase}</h5>
                <p className="phase-title">{phase.description}</p>
                <p className="phase-details">{phase.details}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Signature Strategies */}
        <div className="signature-strategies edu-animate">
          <div className="strategies-header">
            <h4>Signature Learning Strategies</h4>
            <p>Integrated across all subjects, especially in Humanities, Science, and Global Perspectives</p>
          </div>
          <div className="strategies-grid">
            {learningApproach.signatureStrategies.map((strategy, index) => (
              <article className="strategy-card edu-animate" key={strategy.title} style={{ animationDelay: `${index * 100}ms` }}>
                <div className="strategy-media">
                  <img src={strategy.image} alt={strategy.title} />
                  <div className="strategy-overlay">
                    <div className="strategy-icon">
                      {getAssessmentIcon(strategy.icon)}
                    </div>
                  </div>
                </div>
                <div className="strategy-body">
                  <h5>{strategy.title}</h5>
                  <p className="strategy-description">{strategy.description}</p>
                  <ul className="strategy-outcomes">
                    {strategy.outcomes.map((outcome) => (
                      <li key={outcome}>
                        <span className="outcome-bullet">✓</span>
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Integration Note */}
        <div className="integration-note edu-animate">
          <div className="note-content">
            <h4>Integrated Learning Across Subjects</h4>
            <p>{learningApproach.integration}</p>
          </div>
        </div>
      </section>

      <section className="assessment-system" id="assessment-system">
        <div className="assessment-system-header">
          <p className="assessment-system-kicker">Assessment System</p>
          <h3>{assessmentSystem.title}</h3>
          <p>{assessmentSystem.summary}</p>
        </div>

        <div className="assessment-system-intro edu-animate">
          <div className="assessment-intro-content">
            <h4>Our Balanced Approach</h4>
            <p>
              Assessment at Permata Harapan goes beyond traditional testing. We combine multiple methods to capture the full picture of each student's learning journey—from daily growth to mastery of standards.
            </p>
          </div>
        </div>

        <div className="assessment-system-grid">
          {assessmentSystem.points.map((item, index) => (
            <article className="assessment-system-card edu-animate" key={item.title} style={{ animationDelay: `${index * 100}ms` }}>
              <div className="assessment-card-header">
                <div className="assessment-card-icon">
                  {getAssessmentIcon(item.iconType)}
                </div>
                <h4>{item.title}</h4>
              </div>
              <p className="assessment-card-description">{item.description}</p>
              <ul className="assessment-card-details">
                {(item.details || []).map((detail) => (
                  <li key={detail}>
                    <span className="detail-bullet">•</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div className="assessment-methodology edu-animate">
          <div className="methodology-header">
            <h4>Our Assessment Methodology</h4>
            <p>Four pillars that guide how we evaluate and support student learning</p>
          </div>
          <div className="methodology-grid">
            {assessmentSystem.methodology.map((item, index) => (
              <div className="methodology-card" key={item.phase} style={{ animationDelay: `${index * 100}ms` }}>
                <div className="methodology-number">{index + 1}</div>
                <h5>{item.phase}</h5>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="assessment-benefits edu-animate">
          <div className="benefits-header">
            <h4>What This Means for Your Child</h4>
          </div>
          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-icon">
                {AssessmentIcons.book}
              </div>
              <h5>Personalized Learning</h5>
              <p>Each student receives targeted support based on their unique strengths and needs.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                {AssessmentIcons.chart}
              </div>
              <h5>Clear Progress Tracking</h5>
              <p>Regular data helps us identify growth and celebrate achievements along the way.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                {AssessmentIcons.users}
              </div>
              <h5>Open Communication</h5>
              <p>Parents stay informed with detailed reports and regular conferences throughout the year.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">
                {AssessmentIcons.zap}
              </div>
              <h5>Growth Mindset</h5>
              <p>Feedback focuses on effort and improvement, building confidence and resilience.</p>
            </div>
          </div>
        </div>

        <div className="assessment-timeline edu-animate">
          <div className="timeline-header">
            <h4>Assessment Calendar</h4>
            <p>Key assessment touchpoints throughout the academic year</p>
          </div>
          <div className="timeline-cards">
            <div className="timeline-card">
              <div className="timeline-card-header">
                <div className="timeline-card-icon">
                  {AssessmentIcons.calendar}
                </div>
                <div className="timeline-card-period">Aug - Sep</div>
              </div>
              <h5>Diagnostic Baselines</h5>
              <p style={{ textAlign: 'center' }}>Initial assessments to understand each student's starting point.</p>
            </div>
            <div className="timeline-card">
              <div className="timeline-card-header">
                <div className="timeline-card-icon">
                  {AssessmentIcons.clipboard}
                </div>
                <div className="timeline-card-period">Sep - Nov</div>
              </div>
              <h5>Formative Checks & Projects</h5>
              <p style={{ textAlign: 'center' }}>Weekly observations and first performance tasks.</p>
            </div>
            <div className="timeline-card">
              <div className="timeline-card-header">
                <div className="timeline-card-icon">
                  {AssessmentIcons.chart}
                </div>
                <div className="timeline-card-period">Nov - Dec</div>
              </div>
              <h5>Mid-Term Assessment</h5>
              <p style={{ textAlign: 'center' }}>Comprehensive evaluation with parent conferences.</p>
            </div>
            <div className="timeline-card">
              <div className="timeline-card-header">
                <div className="timeline-card-icon">
                  {AssessmentIcons.trending}
                </div>
                <div className="timeline-card-period">Jan - Apr</div>
              </div>
              <h5>Continued Monitoring</h5>
              <p style={{ textAlign: 'center' }}>Ongoing formative assessments and performance tasks.</p>
            </div>
            <div className="timeline-card">
              <div className="timeline-card-header">
                <div className="timeline-card-icon">
                  {AssessmentIcons.award}
                </div>
                <div className="timeline-card-period">May - Jun</div>
              </div>
              <h5>End-Term Assessment</h5>
              <p style={{ textAlign: 'center' }}>Final summative assessments and year-end reports.</p>
            </div>
          </div>
        </div>

        <div className="assessment-cta edu-animate">
          <div className="cta-content">
            <h4>Questions About Assessment?</h4>
            <p>We're happy to discuss how we evaluate student progress and support your child's learning journey.</p>
            <button
              className="assessment-cta-button"
              type="button"
              onClick={() => onNavigate && onNavigate('contact-us', 'contact-form')}
            >
              Schedule a Conversation
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </section>

      <section className="special-programs" id="special-programs">
        <div className="special-programs-header">
          <p className="special-programs-kicker">Programs</p>
          <h3>Bridging, clubs, leadership, community service, ECA, and talent programs</h3>
          <p>
            Programs that extend learning beyond the classroom and strengthen character, talent, and community engagement.
          </p>
          <div className="special-programs-actions">
            <p className="special-programs-note">
              Full schedules and program details are available in Student Life.
            </p>
            <a
              href="#student-life"
              className="special-programs-link"
              onClick={(e) => {
                e.preventDefault();
                onNavigate && onNavigate('student-life', 'programs');
              }}
            >
              View Student Life Programs
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>

        <div className="special-programs-stats edu-animate">
          <div className="special-programs-stat">
            <span className="special-programs-stat-value">6</span>
            <span className="special-programs-stat-label">Program Pathways</span>
          </div>
          <div className="special-programs-stat">
            <span className="special-programs-stat-value">Weekly</span>
            <span className="special-programs-stat-label">Student Activities</span>
          </div>
          <div className="special-programs-stat">
            <span className="special-programs-stat-value">All Levels</span>
            <span className="special-programs-stat-label">Participation Options</span>
          </div>
        </div>

        <div className="special-programs-grid">
          {specialPrograms.map((item) => (
            <article className="special-programs-card edu-animate" key={item.title}>
              <div className="special-programs-media">
                <img src={item.image} alt={item.title} />
              </div>
              <div className="special-programs-body">
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

export default EducationProgramPage;
